import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingBarModule, LoadingBarService } from '@ngx-loading-bar/core';
import { NgxSonnerToaster } from 'ngx-sonner';
import { SplashScreenComponent } from '@component/splash-screen/splash-screen.component';
import { PageErrorComponent } from '@component/page-error/page-error.component';
import { ThemeService } from '@service/core/theme.service';
import { AuthStore } from '@shared/store/auth.store';

@Component({
  selector: 'app-root',
  template: `
    <ngx-loading-bar
      ref="loader"
      [color]="_ngxLoadingBarColor()"
      [includeSpinner]="false"
    />
    @defer (when _isAuthStateLoaded()) {
    <router-outlet />
    <ngx-sonner-toaster
      richColors
      position="top-center"
      [theme]="_ngxSonnerToasterTheme()"
    />
    } @placeholder {
    <app-splash-screen />
    } @loading (minimum 2s) {
    <app-splash-screen />
    } @error {
    <app-page-error />
    }
  `,
  imports: [
    RouterOutlet,
    LoadingBarModule,
    NgxSonnerToaster,
    SplashScreenComponent,
    PageErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  public title = 'win-win-workflow-ui';

  private _router = inject(Router);
  private _loadingBarService = inject(LoadingBarService);
  private _themeService = inject(ThemeService);
  private _authStore = inject(AuthStore);

  private _visitedPages = new Set<string>();

  private _loader = this._loadingBarService.useRef('loader');
  protected _isAuthStateLoaded = this._authStore.isLoaded;

  protected _ngxLoadingBarColor = computed(() =>
    this._themeService.colorCode()
  );
  protected _ngxSonnerToasterTheme = computed(() =>
    this._themeService.isDark() ? 'dark' : 'light'
  );

  constructor() {
    this._handleRouteNavigation();
  }

  private _handleRouteNavigation(): void {
    this._router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (!event.restoredState && !this._visitedPages.has(event.url)) {
          this._loader.start();
        }
      } else if (event instanceof NavigationEnd) {
        this._visitedPages.add(event.urlAfterRedirects);
        this._loader.complete();
      } else if (
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this._loader.complete();
      }
    });
  }
}
